import React from 'react';
import { FieldTextInput, NamedLink, ResponsiveImage, FieldCheckbox } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';
import classNames from 'classnames';
import { createSlug, stringify } from '../../../util/urlHelpers';
import { generateCategorySlug, generateCitySlug } from '../../../util/seo';
import {
    DELIVERY_FEE,
    PICKUP_FEE,
    USAGE_MULTIPLE,
    REQUIRED_UPFRONT,
    feeTypeMapper,
    FLAT_RATE,
    PERCENTAGE_OF_RENTAL,
} from '../../../util/constants';
import { required } from '../../../util/validators';
import { formatMoney, convertPrice, unitDivisor } from '../../../util/currency';
import { organizeFees } from '../../OrderPanel/OrderPanelHelpers';
import { isOncePerCartFee } from '../cartModalHelpers';
import { types as sdkTypes } from '../../../util/sdkLoader';
import QuantityController from './QuantityController';
import css from './CartModalItem.module.css';
import feesCss from '../../OrderPanel/FeesInputFields/FeesInputFields.module.css';

const { Money } = sdkTypes;

/**
 * Component that renders form fields for a single cart item
 * Uses Field components from react-final-form
 */
const CartModalItemFields = props => {
    const {
        item,
        intl,
        form,
        formValues = {},
        fetchLineItemsInProgress,
        currentUserCurrency,
        exchangeRates,
        onRemoveFromCart,
    } = props;

    const {
        id,
        listing,
        quantity: itemQuantity,
    } = item;

    const { attributes, images } = listing || {};
    const { title, publicData } = attributes || {};
    const itemImage = images?.[0] || null;
    const maxQuantity = publicData?.quantity || 1;

    const itemId = id;

    // Extract and organize fees (excluding delivery/pickup, and excluding oncePerCart
    // fees - those render once for the whole cart in CartModalFooter's shared add-ons
    // section instead, same convention as CartDrawerItem's useItemAddons. Without this
    // filter, a fee shared across 2+ items in the cart rendered as an independent
    // checkbox under EACH item, letting a user select it differently per item even
    // though it's meant to apply once to the whole cart.
    const { addons } = React.useMemo(() => {
        const additionalFees = (publicData?.additionalFees || []).filter(
            fee => !isOncePerCartFee(fee)
        );
        const basePrice = listing?.attributes?.price;
        const baseCurrency = basePrice?.currency;

        // Convert the additionalFees rate to the current currency's equivalent rate
        const convertedFees = additionalFees.map(fee => {
            const { rate } = fee;
            if (rate && baseCurrency) {
                const divisor = unitDivisor(baseCurrency);
                const updatedRate = rate * divisor;

                let rateMoney = null;
                if (exchangeRates?.length) {
                    rateMoney = convertPrice(
                        exchangeRates,
                        updatedRate,
                        baseCurrency,
                        currentUserCurrency
                    );
                }

                if (!rateMoney) {
                    rateMoney = new Money(updatedRate, baseCurrency);
                }

                return {
                    ...fee,
                    rate: rateMoney,
                };
            }
            return fee;
        });

        return organizeFees(
            convertedFees,
            DELIVERY_FEE,
            PICKUP_FEE,
            USAGE_MULTIPLE,
            'requiredUpfrontOptional',
            REQUIRED_UPFRONT
        );
    }, [publicData?.additionalFees, exchangeRates, currentUserCurrency, listing]);

    const listingCitySlug = generateCitySlug(publicData?.city);
    const listingCategorySlug = generateCategorySlug(publicData?.category);

    const itemLinkProps = {
        name: 'ListingPage',
        params: {
            ...(listingCitySlug ? { city: listingCitySlug } : {}),
            ...(listingCategorySlug ? { category: listingCategorySlug } : {}),
            id: listing?.id?.uuid,
            slug: createSlug(title || ''),
        },
        to: {
            search: `?${stringify({ cartItemId: id })}`,
        },
    };

    const handleRemove = () => {
        onRemoveFromCart(id);
    };

    return (
        <div className={css.item}>
            {/* Item Image and Title */}
            <div className={css.itemHeader}>
                <div className={css.itemImage}>
                    <NamedLink {...itemLinkProps}>
                        <ResponsiveImage
                            alt={title}
                            image={itemImage}
                            variants={['square-small2x', 'square-small']}
                        />
                    </NamedLink>
                </div>
                <div  className={css.mainContentWrapper}>
                <div className={css.itemTitle}>
                    <NamedLink {...itemLinkProps}>{title}</NamedLink>
                </div>
                            {/* Additional Fees (excluding delivery/pickup) */}
            {(addons?.usageMultipleCharges?.length > 0 ||
                addons?.optionalCharges?.length > 0 ||
                addons?.requiredCharges?.length > 0) && (
                    <div className={css.feesSection}>
                        {/* <h5 className={css.feesTitle}>
                            <FormattedMessage id="CartModal.additionalFees" />
                        </h5> */}

                        {/* Usage Multiple Charges */}
                        {addons.usageMultipleCharges.map(charge => {
                            const { feeType, rate, usageLabel, whenToApply } = charge;
                            const isRequired = whenToApply === REQUIRED_UPFRONT;
                            const fieldName = `items.${itemId}.${feeType}`;

                            return (
                                <FieldTextInput
                                    key={feeType}
                                    type="number"
                                    className={feesCss.usageField}
                                    min={1}
                                    id={`${itemId}_${feeType}`}
                                    name={fieldName}
                                    label={intl.formatMessage(
                                        { id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.label' },
                                        {
                                            feeType: feeTypeMapper[feeType] || feeType,
                                            text: usageLabel,
                                            rate: formatMoney(intl, rate),
                                            isRequired,
                                        }
                                    )}
                                    {...(isRequired
                                        ? {
                                            validate: required(
                                                intl.formatMessage({
                                                    id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.required',
                                                })
                                            ),
                                        }
                                        : {})}
                                    disabled={fetchLineItemsInProgress}
                                />
                            );
                        })}

                        {/* Optional Charges - Use FieldArray */}
                        {addons.optionalCharges.length > 0 && (
                            <div className={feesCss.otherFeesContainer}>
                                {addons.optionalCharges.map(fee => {
                                    const isChecked = formValues?.optionalFees?.includes(fee.feeType) || false;
                                    return (
                                        <div
                                            key={fee.feeType}
                                            className={classNames(
                                                feesCss.otherFee,
                                                fetchLineItemsInProgress ? feesCss.disabled : null,
                                                isChecked ? feesCss.active : null
                                            )}
                                        >
                                            <FieldCheckbox
                                                id={`${itemId}_${fee.feeType}`}
                                                name={`items.${itemId}.optionalFees`}
                                                value={fee.feeType}
                                                disabled={fetchLineItemsInProgress}
                                                className={feesCss.checkbox}
                                                label={
                                                    <div className={feesCss.feeLabel}>
                                                        <div>
                                                            <div className={feesCss.deliveryText}>
                                                                <FormattedMessage
                                                                    id="OtherOptionalChargesField.OtherOptionalFees.label"
                                                                    values={{ feeType: fee.feeType }}
                                                                />
                                                            </div>
                                                            <div className={feesCss.feeDescription}>{fee.feeDescription}</div>
                                                        </div>
                                                        <span className={feesCss.priceText}>
                                                            {fee.feeCalculationType === FLAT_RATE
                                                                ? formatMoney(intl, fee.rate)
                                                                : fee.feeCalculationType === PERCENTAGE_OF_RENTAL
                                                                    ? intl.formatMessage(
                                                                        {
                                                                            id: 'OtherOptionalChargesField.OtherOptionalFees.percentage',
                                                                        },
                                                                        { percentage: fee.percentage }
                                                                    )
                                                                    : null}
                                                        </span>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Required Charges - Use FieldArray */}
                        {addons.requiredCharges.length > 0 && (
                            <div className={feesCss.otherFeesContainer}>
                                {addons.requiredCharges.map(fee => (
                                    <div
                                        key={fee.feeType}
                                        className={classNames(feesCss.otherFee, feesCss.disabled, feesCss.active)}
                                    >
                                        <FieldCheckbox
                                            id={`${itemId}_required_${fee.feeType}`}
                                            name={`items.${itemId}.requiredFees`}
                                            value={fee.feeType}
                                            disabled={true}
                                            checked={true}
                                            label={
                                                <div className={feesCss.feeLabel}>
                                                    <div>
                                                        <div className={feesCss.deliveryText}>
                                                            <FormattedMessage
                                                                id="RequiredChargesField.label"
                                                                values={{ feeType: fee.feeType }}
                                                                defaultMessage="{feeType}"
                                                            />
                                                        </div>
                                                        <div className={feesCss.feeDescription}>{fee.feeDescription}</div>
                                                    </div>
                                                    <span className={feesCss.priceText}>
                                                        {fee.feeCalculationType === FLAT_RATE
                                                            ? formatMoney(intl, fee.rate)
                                                            : fee.feeCalculationType === PERCENTAGE_OF_RENTAL
                                                                ? intl.formatMessage(
                                                                    {
                                                                        id: "RequiredChargesField.percentage",
                                                                        defaultMessage: "{percentage}%",
                                                                    },
                                                                    { percentage: fee.percentage }
                                                                )
                                                                : null}
                                                    </span>
                                                </div>
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

            {/* Quantity Controller */}
            <div className={css.quantitySection}>
                <QuantityController
                    quantity={formValues?.quantity || itemQuantity || 1}
                    maxQuantity={maxQuantity}
                    form={form}
                    fieldName={`items.${itemId}.quantity`}
                    disabled={fetchLineItemsInProgress}
                />
            </div>
                </div>
            
                <button
                    className={css.removeButton}
                    onClick={handleRemove}
                    type="button"
                    aria-label={intl.formatMessage({ id: 'CartModal.removeItem' })}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M11.75 3.5H15.5V5H14V14.75C14 15.1642 13.6642 15.5 13.25 15.5H2.75C2.33579 15.5 2 15.1642 2 14.75V5H0.5V3.5H4.25V1.25C4.25 0.83579 4.58579 0.5 5 0.5H11C11.4142 0.5 11.75 0.83579 11.75 1.25V3.5ZM12.5 5H3.5V14H12.5V5ZM5.75 7.25H7.25V11.75H5.75V7.25ZM8.75 7.25H10.25V11.75H8.75V7.25ZM5.75 2V3.5H10.25V2H5.75Z"
                            fill="#6D6D6D"
                        />
                    </svg>
                </button>
            </div>

    
        </div>
    );
};

export default CartModalItemFields;
